import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	Module,
	Button,
	Form,
	LookupInput,
	FormItem,
	Icon,
	ParentBoundComponent,
	DateUtils,
	TabPane,
	showMessageBox
} from 'ps-gui';

import {
	getColumnText,
	getColumnTextMap,
	getPhrase
} from '../data/phrases';
import MyRecordingsModel, { getSearchOutputColumns } from './myrecordingsmodel';
import {
	CellBubble,
	CellMainText,
	CellSubText
} from './portalresultsetmodule';
import {
	exportToCsv,
	getGlobalContextChangeChecker,
	parseRoleId
} from './utils';
import { ReactComponent as ModuleLogo } from '../svg/myrecordings.svg';
import {
	ModuleBodySection,
	ModuleHeaderSection,
	ModuleHeader,
	ModuleHeaderBlock
} from '../blocks/modulesections';
import { withGlobalContext } from '../data/globalcontext';
import Message from '../blocks/message';
import CheckButton from '../components/checkbutton';
import { getLookup } from '../data/lookup';
import { StatusBubble } from './submissioncomponents';
import { renderDateTimeCell } from './commoncellrenderers';
import { getRecordingProtocolInfoFromAppState, isRecordingProtocolModifiedFromAppState } from './recordingprotocol/recordingprotocoldetailmodel';
import memoizeOne from 'memoize-one';

const formatDate = DateUtils.formatDate;

const Wrapper = styled.div`
	.ps-cross {
		.ps-tabularInput {
			flex: 1 1 0px;
		}
	}


	.ps-button[data-name=deleteRecording][type=full] {
		background: ${props => props.theme.fullNegativeButtonBackground};
		border: ${props => props.theme.fullNegativeButtonBorder};
	}

	.ps-button[data-name=deleteRecording][type=medium] {
		color: ${props => props.theme.mediumNegativeButtonColor};
		border: ${props => props.theme.mediumNegativeButtonBorder};
		&:hover {
			color: ${props => props.theme.mediumNegativeButtonHoverColor};
			border: ${props => props.theme.mediumNegativeButtonHoverBorder};
		}
	}
`;

//mediumNegativeButtonHoverBackground: 'none',


const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);


const TabText = styled.span`

	.icon {
		position: relative;
		top: -1px;
	}

	.icon.left {
		margin-right: 8px;
	}

	.icon[data-name=close] {
		margin-left: 16px;
		* {
			fill: #4FC098;
		}
	}
`;


class MyRecordingsModule extends ParentBoundComponent {

	constructor() {
		super();
		this.exportToCsv = this.exportToCsv.bind(this);
		this.setRoleId = this.setRoleId.bind(this);
		this.setDistributionPeriodId = this.setDistributionPeriodId.bind(this);

		this.checkGlobalContextChange = getGlobalContextChangeChecker(this);
		this.onTabSelected = this.onTabSelected.bind(this);
		this.registerNewRecording = this.registerNewRecording.bind(this);
		this.processTabs = memoizeOne(this._processTabs.bind(this));
		this.canRegisterRecording = !getLookup('recordingProtocolPartyRole').isEmpty();
		this.closeRecordingProtocolDetail = this.closeRecordingProtocolDetail.bind(this);
		this.onRecordingProtocolRowAction = this.onRecordingProtocolRowAction.bind(this);
		this.onPermanentCatalogRecordingRowAction = this.onPermanentCatalogRecordingRowAction.bind(this);
	}

	async closeRecordingProtocolDetail() {
		if (isRecordingProtocolModifiedFromAppState(this.getData().get('recordingProtocolDetail'))) {
			const dlgRes = await showMessageBox(getPhrase('reallyCloseModifiedRecordingProtocolDetail'), {
				type: 'confirm',
				yesIsNegative: true
			});
			if (dlgRes !== 'yes') {
				return;
			}
		}
		this.setModel(this.getModel().closeRecordingProtocolDetail());
	}

	async checkPermanentCatalogRecordingModified() {
		const rpd = this.getData().getIn(['permanentCatalogDetail', 'data']) || EMPTY_MAP;
		if (rpd.get('detail') !== rpd.get('originalDetail')) {
			const dlgRes = await showMessageBox(getPhrase('reallyCloseModifiedPermanentCatalogRecordingDetail'), {
				type: 'confirm',
				yesIsNegative: true
			});
			if (dlgRes !== 'yes') {
				return false;
			}
		}
		return true;
	}

	async closePermanentCatalogDetail() {
		if (!await this.checkPermanentCatalogRecordingModified()) {
			return;
		}
		this.setModel(this.getModel().closePermanentCatalogDetail());
	}


	registerNewRecording() {
		this.setModel(this.getModel().openRecordingProtocolDetail());
	}

	onTabSelected(tabId) {
		this.setModel(this.getModel().setSelectedTabId(tabId));
	}

	async setRoleId(roleId) {
		let model = this.getModel().setRoleId(roleId);
		this.setModel(await model.refreshSearch());
	}

	async setDistributionPeriodId(dpId) {
		let model = this.getModel().setDistributionPeriodId(dpId);
		this.setModel(await model.refreshSearch());
	}

	_processTabs(tabs) {
		// recordingRegistration
		return tabs.map(tab => {
			const tabId = tab.get('id');
			let text = tab.get('text');
			if (tabId === 'recordingProtocolDetail') {
				const rpInfo = getRecordingProtocolInfoFromAppState(this.getData().get('recordingProtocolDetail'));
				if (rpInfo.recordingProtocolId) {
					text = rpInfo.isrc || rpInfo.title;
				} else {
					text = getPhrase('recordingRegistration');
				}
				text =
					<TabText>
						<Icon
							//name={tabId}
							className="left"
						/>
						{text}
						<Icon
							name="close"
							active
							onMouseDown={e => {
								e.preventDefault();
								window.setTimeout(() => {
									this.closeRecordingProtocolDetail();
								}, 0);
							}}
						/>
					</TabText>;

			} else if (tabId === 'permanentCatalogDetail') {
				const pcd = this.getData().getIn(['permanentCatalogDetail', 'data', 'detail']) || EMPTY_MAP;
				const isrc = pcd.get('isrc');
				const title = pcd.get('title');
				if (isrc) {
					text = isrc;
				} else if (title) {
					text = title;
				} else {
					text = getPhrase('permanentCatalogDetail');
				}
				text =
					<TabText>
						<Icon
							//name={tabId}
							className="left"
						/>
						{text}
						<Icon
							name="close"
							active
							onMouseDown={e => {
								e.preventDefault();
								window.setTimeout(() => {
									this.closePermanentCatalogDetail();
								}, 0);
							}}
						/>
					</TabText>;
			}
			return tab.set('text', text);
		});
	}


	exportToCsv() {
		let cols = getSearchOutputColumns(true);
		const data = this.getData();
		const roleId = data.get('roleId');
		const role = parseRoleId(roleId);
		const isTabularSourceProducer = (data.get('tabularSourceProducers') || EMPTY_LIST).contains(role.partyId);
		if (isTabularSourceProducer) {
			cols = [
				'producer',
				'label',
				'title',
				'subtitle',
				'alt_title',
				'isrc',
				'album',
				'runtime_min',
				'runtime_sec',
				'recording_type_id',
				'licence_share_pct',
				'main_artist',
				'performer1_name',
				'performer1_share_pct',
				'performer1_performance',
				'performer2_name',
				'performer2_share_pct',
				'performer2_performance',
				'performer3_name',
				'performer3_share_pct',
				'performer3_performance',
				'performer4_name',
				'performer4_share_pct',
				'performer4_performance',
				'performer5_name',
				'performer5_share_pct',
				'performer5_performance',
				'author_music',
				'author_lyrics',
				'genre_id',
				'production_year',
				'release_year',
				'recording_country_id',
				'licence_from_date',
				'licence_to_date',
				'ai_generated',
			];
			exportToCsv(this.getModel().getSearch(), {
				outputColumns: cols,
				columnTextMap: getColumnTextMap(cols, 'tabularCatalog'),
				url: isTabularSourceProducer ? '/api/dq/sv_permanent_catalog_recording_tabular_export' : null
			});
		} else {
			exportToCsv(this.getModel().getSearch(), {
				outputColumns: cols,
				columnTextMap: getColumnTextMap(cols),
			});
		}
	}

	getModel() {
		return new MyRecordingsModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	componentDidMount() {
		this.checkGlobalContextChange();
	}

	componentDidUpdate(prevProps) {
		this.checkGlobalContextChange(prevProps);
	}

	renderNoData() {
		return (
			<Message>
				{getPhrase('myRecordingsNoDataMessage')}
			</Message>
		);
	}

	async setSource(name, value) {
		this.setModel(await this.getModel().setSource(name, value));
	}

	async onRecordingProtocolRowAction(row, rowIndex, options) {
		const id = row.get('recording_protocol_id');
		this.setModel(await this.getModel().loadRecordingProtocolDetail(id));
	}

	async onPermanentCatalogRecordingRowAction(row, rowIndex, options) {
		if (!await this.checkPermanentCatalogRecordingModified()) {
			return;
		}
		const producerId = row.get('producer_id');
		const recordingId = row.get('recording_id');
		if (!producerId || !recordingId) {
			return;
		}
		this.setModel(await this.getModel().openPermanentCatalogDetail(producerId, recordingId));
	}

	getSelectedTabId() {
		return this.getData().get('selectedTabId') || 'registeredRecordings';
	}

	renderTabPane() {
		const tabs = this.processTabs(this.getData().get('tabs') || EMPTY_LIST);
		return (
			<TabPane
				selectedTabId={this.getSelectedTabId()}
				tabs={tabs}
				onTabSelected={this.onTabSelected}
			>
				<Block24 />
			</TabPane>
		);
	}

	renderRecordings() {
		const data = this.getData();
		const roleId = data.get('roleId');
		const isProducer = roleId && roleId.endsWith('PRO');
		const sources = data.get('sources') || EMPTY_MAP;
		const mode = data.get('mode');
		return (
			<ModuleBodySection>
				<RowCountEtc>
					<RowCount>
						<span className="label">{getPhrase('recordingCount')}:</span>
						<span className="value">{data.getIn([
							'search',
							'data',
							'result',
							'data',
							'pager',
							'totalCount'
						])}</span>
					</RowCount>
					<Button
						type="medium"
						text={getPhrase('exportSelectionToCSV')}
						onAction={this.exportToCsv}
					/>
					{
						this.canRegisterRecording && (
							<Button
								type="medium"
								text={getPhrase('registerNewRecording')}
								onAction={this.registerNewRecording}
							/>
						)
					}
				</RowCountEtc>
				{this.renderTabPane()}
				{
					mode === 'performer' && (
						<QuickFilter>
							<QuickFilterHeader>{getPhrase('displayedRecordings')}</QuickFilterHeader>
							<CheckButton
								text={getPhrase('recordingSource.isrcProtocol')}
								rightIcon="isrcProtocol"
								value={sources.get('isrcProtocol')}
								onChange={value => this.setSource('isrcProtocol', value)}
							/>
							<CheckButton
								text={getPhrase('recordingSource.other')}
								value={sources.get('other')}
								onChange={value => this.setSource('other', value)}
							/>
						</QuickFilter>
					)
				}
				<Module
					appState={data.get('search')}
					onDataChange={newData => {
						this.setData(data.setIn(['search', 'data'], newData));
					}}
					customResultProps={customResultProps}
					onRowAction={isProducer ? this.onPermanentCatalogRecordingRowAction : null}
				//leftToolBar={this.renderCandidateListToolbar}
				//onResultIconMouseDown={this.onCandidateIconMouseDown}
				/>
			</ModuleBodySection>
		);
	}

	renderProtocolList() {
		const data = this.getData();
		const sources = data.get('sources') || EMPTY_MAP;
		const mode = data.get('mode');
		return (
			<ModuleBodySection>
				<RowCountEtc>
					<RowCount>
						<span className="label">{getPhrase('registrationCount')}:</span>
						<span className="value">{data.getIn([
							'recordingProtocolSearch',
							'data',
							'result',
							'data',
							'pager',
							'totalCount'
						])}</span>
					</RowCount>
					{
						this.canRegisterRecording && (
							<Button
								type="medium"
								text={getPhrase('registerNewRecording')}
								onAction={this.registerNewRecording}
							/>
						)
					}
				</RowCountEtc>
				{this.renderTabPane()}
				<Module
					appState={data.get('recordingProtocolSearch')}
					onDataChange={newData => {
						this.setData(data.setIn(['recordingProtocolSearch', 'data'], newData));
					}}
					customResultProps={registrationCustomResultProps}
					onRowAction={this.onRecordingProtocolRowAction}
				/>
			</ModuleBodySection>
		);
	}

	renderProtocolDetail() {
		const data = this.getData();
		const detailAppState = data.get('recordingProtocolDetail');
		return (
			<ModuleBodySection>
				<RowCountEtc>
					<RowCount>{getPhrase('recordingRegistration')}</RowCount>
					{
						/*!isRecordingProtocolDetailReadOnly(detailAppState.get('data')) && (
							<Button
								type="medium"
								negative
								text={getPhrase('cancelRecordingRegistration')}
								onAction={this.cancelRecordingProtocolEntry}
							/>
						)*/
					}
				</RowCountEtc>
				{this.renderTabPane()}
				<Module
					appState={detailAppState}
					onDataChange={newData => {
						this.setData(data.setIn(['recordingProtocolDetail', 'data'], newData));
					}}
				/>
			</ModuleBodySection>
		);
	}

	renderPermanentCatalogDetail() {
		const data = this.getData();
		const detailAppState = data.get('permanentCatalogDetail');
		return (
			<ModuleBodySection>
				<RowCountEtc>
					<RowCount>{getPhrase('permanentCatalogDetail')}</RowCount>
					{
						/*!isRecordingProtocolDetailReadOnly(detailAppState.get('data')) && (
							<Button
								type="medium"
								negative
								text={getPhrase('cancelRecordingRegistration')}
								onAction={this.cancelRecordingProtocolEntry}
							/>
						)*/
					}
				</RowCountEtc>
				{this.renderTabPane()}
				<Module
					appState={detailAppState}
					onDataChange={newData => {
						this.setData(data.setIn(['permanentCatalogDetail', 'data'], newData));
					}}
				/>
			</ModuleBodySection>
		);
	}

	render() {
		const model = this.getModel();
		const data = this.getData();
		const selectedTabId = this.getSelectedTabId();
		const roleId = data.get('roleId');
		const roleLookup = data.get('roleLookup');
		const dpLookup = data.get('distributionPeriodLookup.' + roleId);
		const languageTimestamp = model.getLanguageTimestamp();
		if (!roleLookup) {
			return null;
		}
		if (roleLookup.isEmpty()) {
			return this.renderNoData();
		}
		return (
			<Wrapper>
				<ModuleHeaderSection>
					<ModuleHeaderBlock>
						<ModuleHeader>{getPhrase('MyRecordings')}</ModuleHeader>
						<Form virtual horizontal>
							<FormItem label={getColumnText('personGroup')} fixedWidth="470px" marginRight="30px">
								<LookupInput
									getText={roleLookup.getText}
									getRows={roleLookup.getRows}
									combo
									mandatory
									value={roleId}
									onChange={this.setRoleId}
									textRefreshTimestamp={languageTimestamp}
								/>
							</FormItem>
							{
								dpLookup &&
								<FormItem label={getColumnText('distribution_period')} fixedWidth="170px">
									<LookupInput
										getText={dpLookup.getText}
										getRows={dpLookup.getRows}
										combo
										mandatory
										value={data.get('distributionPeriodId')}
										onChange={this.setDistributionPeriodId}
										textRefreshTimestamp={languageTimestamp}
									/>
								</FormItem>

							}
						</Form>
					</ModuleHeaderBlock>
					<ModuleHeaderBlock>
						<ModuleLogo />
					</ModuleHeaderBlock>
				</ModuleHeaderSection>
				{
					selectedTabId === 'registeredRecordings' ?
						this.renderRecordings() : selectedTabId === 'recordingProtocolDetail' ?
							this.renderProtocolDetail() : selectedTabId === 'permanentCatalogDetail' ?
								this.renderPermanentCatalogDetail() : this.renderProtocolList()
				}
			</Wrapper>
		);
	}
}

const QuickFilterHeader = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: ${props => props.theme.disabledTextColor};
	margin-bottom: 8px;
`;

const QuickFilter = styled.div`
	margin-bottom: 24px;
	.checkButton {
		margin-right: 16px;
	}
`;

const RowCountEtc = styled.div`
	margin-bottom: 24px;
`;

const RowCount = styled.div`
	display: inline-block;
	color: ${props => props.theme.disabledTextColor};
	font-size: 20px;
	margin-right: 40px;
	.label {
		font-weight: 400;
		margin-right: 5px;
	}
	.value {
		font-weight: 900;
	}
`;

const FlexCell = styled.div`
	display: flex;
	align-items: center;
`;

const FlexCellContent = styled.div`
	flex-grow: ${props => props.flexGrow};
	flex-shrink: ${props => props.flexShrink};
`;

const LicenceCell = styled.div`
	.range {
		margin-bottom: 4px;
		.date {
			display: inline-block;
			&.from {
				margin-right: 4px;
			}
			&.to {
				margin-left: 4px;
			}
		}
	}
`;

const cellRendererMap = Object.freeze({
	'recording': (cell, row, colIndex) => {
		const subtitle = row.get('subtitle');
		const isIsrcProtocol = row.get('is_isrc_protocol') === 'Y';
		const isModified = row.get('is_modified') === 'Y';
		return (
			<FlexCell>
				<FlexCellContent flexGrow="1">
					<CellMainText>{cell}</CellMainText>
					{
						!!subtitle &&
						<CellSubText>{subtitle}</CellSubText>

					}
					<CellSubText>{row.get('recording_id')}</CellSubText>
				</FlexCellContent>
				{
					(isIsrcProtocol || isModified) &&
					<FlexCellContent>
						<Icon
							name={isIsrcProtocol ? 'isrcProtocol' : null}
						/>
						<Icon
							name={isModified ? 'edit' : null}
							hint={getPhrase('recordingModificationsAwaitProcessing')}
						/>
					</FlexCellContent>

				}
			</FlexCell>
		);
	},
	licence: (cell, row) => {
		if (!cell) {
			return null;
		}
		return (
			<LicenceCell>
				{
					cell.split(',').map((range, index) => {
						const idx = range.indexOf('-');
						if (idx < 0) {
							return null;
						}
						const fromDate = range.substring(0, idx);
						const toDate = range.substring(idx + 1);
						return (
							<div key={index} className='range'>
								{getColumnText('from.lower')} <span className='from date'>{formatDate(fromDate)}</span>
								{
									toDate && (
										<>
											{getColumnText('to.lower')}
											<span className='to date'>{formatDate(toDate)}</span>
										</>
									)
								}
							</div>
						);
					})
				}
			</LicenceCell>
		);
	},
	'production_release_year': (cell, row, colIndex) => {
		if (row.get('is_release_year')) {
			return '(' + cell + ')';
		} else {
			return cell;
		}
	},
	'audio_video': (cell, row, colIndex) => {
		if (!cell) {
			return null;
		} else if (cell !== 'A' && cell !== 'V') {
			cell = 'Other';
		}
		const id = 'recordingType' + cell;
		return (
			<CellBubble className={id}>
				{getPhrase(id)}
			</CellBubble>
		);
	},
	'role_id': (cell, row, colIndex) => {
		return (
			<>
				<CellMainText className="secondary">{cell}</CellMainText>
				<CellSubText>{row.get('role_type')}</CellSubText>
			</>
		);
	},
	'submission_status': (cell, row) => {
		return (
			<StatusBubble className={row.get('submission_status_id')}>{cell}</StatusBubble>
		);
	},
	last_modified: renderDateTimeCell
});



const customResultProps = {
	cellRendererMap,
	rowCountLabel: () => getPhrase('recordingCount'),
	withRowSelection: true,
	defaultColumnWidths: {
		'audio_video': '120px',
		'length': '120px',
		'isrc': '140px',
		'production_release_year': '120px',
		'main_artist': '220px',
		'licence': '230px',
		'submission_type': '140px',
		last_modified: '120px',
		'submission_status': '180px',
	},
	columnFilters: {
		'audio_video': {
			type: 'lookup',
			lookup: 'audioVideo'
		},
		'isrc': {
			type: 'text'
		},
		'length': {
			type: 'runtime'
		},
		'production_release_year': {
			type: 'number',
			groupless: true,
			inputSize: 4,
		},
		'recording': {
			type: 'text'
		},
		'main_artist': {
			type: 'text'
		},
		'submission_status': {
			type: 'lookup',
			lookup: 'recordingProtocolSubmissionStatus'
		},
		'submission_type': {
			type: 'lookup',
			lookup: 'recordingProtocolSubmissionType'
		},
		last_modified: {
			type: 'date'
		},
		'role_id': {
			type: 'lookup',
			lookup: 'recordingProtocolPartyRole'
		}
	}
};

const registrationCustomResultProps = (function() {
	let res = Object.assign({}, customResultProps);
	res.withRowSelection = true;
	res.rowCountLabel = () => getPhrase('registrationCount');
	return res;
})();


const Block24 = styled.div`
	height: 24px;
`;

export default withGlobalContext(MyRecordingsModule);

